import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDateTime } from '../../utils/formatDateTime';

interface Invoice {
  _id: string;
  invoiceNumber: string;
  type: 'delivery' | 'return' | 'purchase';
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  items: Array<{
    product: {
      _id: string;
      name: string;
      sku: string;
    };
    quantity: number;
    unitPrice: number;
    total: number;
    warrantyDetails?: {
      hasWarranty: boolean;
      serviceWarranty?: number | null;
      sparePartsWarranty?: number | null;
      compressorWarranty?: number | null;
      warranty?: number | null;
    };
  }>;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  dueDate?: string;
  issueDate: string;
  project?: {
    _id: string;
    name: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { settings } = useSettings();
  const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiService.get(`/invoices/${id}`);
        setInvoice(res.data);
      } catch (err) {
        setError('Failed to load invoice details.');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }
  if (error) {
    return <div className="text-center text-red-600 p-8">{error}</div>;
  }
  if (!invoice) {
    return <div className="text-center text-gray-500 p-8">Invoice not found.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <Card className="dark:bg-gray-900">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2 text-black dark:text-white">Invoice #{invoice.invoiceNumber}</h2>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 mb-2">
            Status: {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
          </span>
          <p className="text-gray-600 dark:text-gray-300 mb-2">Type: {invoice.type}</p>
          {invoice.project && (
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Project: <Link to={`/projects/${invoice.project._id}`} className="text-blue-600 underline">{invoice.project.name}</Link>
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="mb-2 text-gray-700 dark:text-gray-200">Customer Name:</div>
            <div className="font-medium text-black dark:text-white">{invoice.customerName || 'N/A'}</div>
          </div>
          <div>
            <div className="mb-2 text-gray-700 dark:text-gray-200">Customer Email:</div>
            <div className="font-medium text-black dark:text-white">{invoice.customerEmail || 'N/A'}</div>
          </div>
          <div>
            <div className="mb-2 text-gray-700 dark:text-gray-200">Customer Phone:</div>
            <div className="font-medium text-black dark:text-white">{invoice.customerPhone || 'N/A'}</div>
          </div>
          <div>
            <div className="mb-2 text-gray-700 dark:text-gray-200">Customer Address:</div>
            <div className="font-medium text-black dark:text-white">{invoice.customerAddress || 'N/A'}</div>
          </div>
          <div>
            <div className="mb-2 text-gray-700 dark:text-gray-200">Issue Date:</div>
            <div className="font-medium text-black dark:text-white">{invoice.issueDate ? formatDateTime(invoice.issueDate, settings) : 'N/A'}</div>
          </div>
          <div>
            <div className="mb-2 text-gray-700 dark:text-gray-200">Due Date:</div>
            <div className="font-medium text-black dark:text-white">{invoice.dueDate ? formatDateTime(invoice.dueDate, settings) : 'N/A'}</div>
          </div>
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Items</h3>
          <DoubleScrollbar>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-left">SKU</th>
                  <th className="px-4 py-2 text-left">Qty</th>
                  <th className="px-4 py-2 text-left">Unit Price</th>
                  <th className="px-4 py-2 text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center text-gray-500 py-2">No items.</td></tr>
                )}
                {(invoice.items || []).map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-2">
                      <div className="font-medium">{item.product?.name || 'N/A'}</div>
                      {item.warrantyDetails?.hasWarranty && (
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          <div className="font-semibold underline">Warranty Details:</div>
                          {item.warrantyDetails.serviceWarranty !== null && item.warrantyDetails.serviceWarranty !== undefined && (
                            <div>Service: {item.warrantyDetails.serviceWarranty} Years</div>
                          )}
                          {item.warrantyDetails.sparePartsWarranty !== null && item.warrantyDetails.sparePartsWarranty !== undefined && (
                            <div>Spare Parts: {item.warrantyDetails.sparePartsWarranty} Years</div>
                          )}
                          {item.warrantyDetails.compressorWarranty !== null && item.warrantyDetails.compressorWarranty !== undefined && (
                            <div>Compressor: {item.warrantyDetails.compressorWarranty} Years</div>
                          )}
                          {item.warrantyDetails.warranty !== null && item.warrantyDetails.warranty !== undefined && (
                            <div>General: {item.warrantyDetails.warranty} Years</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">{item.product?.sku || 'N/A'}</td>
                    <td className="px-4 py-2">{item.quantity}</td>
                    <td className="px-4 py-2">
                      {currencySymbol}{item.unitPrice?.toLocaleString('en-BD') ?? 'N/A'}
                    </td>
                    <td className="px-4 py-2">
                      {currencySymbol}{item.total?.toLocaleString('en-BD') ?? 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DoubleScrollbar>
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Totals</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><span className="font-semibold">Subtotal:</span> <span className="text-gray-700 dark:text-gray-200">
              {currencySymbol}{invoice.subtotal?.toLocaleString('en-BD') ?? 'N/A'}
            </span></div>
            <div><span className="font-semibold">Tax:</span> <span className="text-gray-700 dark:text-gray-200">
              {currencySymbol}{invoice.taxAmount?.toLocaleString('en-BD') ?? 'N/A'}
            </span></div>
            <div><span className="font-semibold">Discount:</span> <span className="text-gray-700 dark:text-gray-200">
              {currencySymbol}{invoice.discountAmount?.toLocaleString('en-BD') ?? 'N/A'}
            </span></div>
            <div><span className="font-semibold">Total:</span> <span className="text-gray-700 dark:text-gray-200">
              {currencySymbol}{invoice.totalAmount?.toLocaleString('en-BD') ?? 'N/A'}
            </span></div>
          </div>
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Other Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><span className="font-semibold">Notes:</span> <span className="text-gray-700 dark:text-gray-200">{invoice.notes || 'N/A'}</span></div>
            <div><span className="font-semibold">Created At:</span> <span className="text-gray-700 dark:text-gray-200">{formatDateTime(invoice.createdAt, settings)}</span></div>
            <div><span className="font-semibold">Updated At:</span> <span className="text-gray-700 dark:text-gray-200">{formatDateTime(invoice.updatedAt, settings)}</span></div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InvoiceDetailPage; 